import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AuthShell, IconInput } from "./AuthShell";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2, User, Mail, Lock, Phone } from "lucide-react";
import { isValidEmail } from "../lib/validate";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidEmail(email)) { setError("Enter a valid email address."); return; }
    setLoading(true);
    const res = await register(name, email, password, phone.trim());
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    if (res.immediate) navigate("/dashboard", { replace: true });
    else setPending(res.message || "Account created. Awaiting CEO approval.");
  };

  if (pending) {
    return (
      <AuthShell>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center" data-testid="register-pending">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">Check your email</h2>
          <p className="text-sm text-slate-500 mt-2">{pending}</p>
          <Button asChild className="mt-6 bg-[#1E3A8A] hover:bg-[#1D4ED8]">
            <Link to="/login">Back to Sign In</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Create an Account</h2>
        <p className="text-sm text-slate-500 mt-1">Activate your account by email, then the CEO assigns your role.</p>

        {error && (
          <div data-testid="register-error" className="mt-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <form onSubmit={submit} noValidate className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Full Name</Label>
            <IconInput icon={User} id="name" data-testid="register-name" required value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Jean Silas" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <IconInput icon={Mail} id="email" data-testid="register-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@silastech.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <IconInput icon={Lock} id="password" data-testid="register-password" type="password" required minLength={8} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">Mobile number <span className="text-slate-400 font-normal">(optional — for SMS 2FA)</span></Label>
            <IconInput icon={Phone} id="phone" data-testid="register-phone" type="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)} placeholder="+237677123456" />
          </div>
          <Button type="submit" data-testid="register-submit" disabled={loading}
            className="w-full bg-[#1E3A8A] hover:bg-[#1D4ED8] h-11">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create Account
          </Button>
        </form>

        <p className="mt-6 text-sm text-center text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-700 font-semibold hover:underline">Sign In</Link>
        </p>
      </div>
    </AuthShell>
  );
}
